import { defineStore } from 'pinia';
import { ref } from 'vue';
import { InspectionStatuses } from '@/store/inspections';
import {
  getLocalstorageItem,
  localStorageKeys,
  removeLocalstorageItem,
  setLocalstorageItem,
} from '@/helpers/localStorage';
import { useApi } from '@/composables/useApi';

export const InstallationStatuses = {
  New: 'new',
  Signed: 'signed',
  Needs_Prepayment: 'has prepayment',
  Prepaid: 'prepaid',
  Planned: 'planned',
  Delivered: 'delivered',
  Paid: 'paid',
  InspectionPlanned: 'inspection planned',
  NetOperator: 'inspection approved',
  Completed: 'completed',
  Ordered: 'ordered',
  ToSignIChoosr: 'to sign iChoosr',
  ToSurvey: 'to survey',
};

export const useInstallationStore = defineStore('installationStore', () => {
  const { apiGet } = useApi();

  const loading = ref(false);
  const installations = ref([]);

  async function getInstallations() {
    const localStorageInstallations = getLocalstorageItem(localStorageKeys.installations);

    if (localStorageInstallations) {
      installations.value = localStorageInstallations;
      return;
    }

    return await fetchInstallations();
  }

  async function fetchInstallations() {
    if (loading.value) {
      return;
    }

    loading.value = true;

    try {
      installations.value = await apiGet('installations');
      setLocalstorageItem(localStorageKeys.installations, installations.value);
    } finally {
      loading.value = false;
    }
  }

  async function refreshInstallations() {
    removeLocalstorageItem(localStorageKeys.installations);
    await getInstallations();
  }

  async function getInstallationByID(id) {
    await getInstallations();
    return installations.value.find((installation) => installation.id === Number(id));
  }

  async function getInstallationsByResourceID(resourceIdName, id) {
    await getInstallations();
    return installations.value.filter((installation) => installation[resourceIdName] === Number(id));
  }

  async function getInstallationByResourceID(resourceIdName, id) {
    const installations = await getInstallationsByResourceID(resourceIdName, id);

    if (installations.length === 1) {
      return installations[0];
    }

    return null;
  }

  async function getInstallationRouteByResourceID(resourceIdName, id) {
    const installation = await getInstallationByResourceID(resourceIdName, id);
    return installation
      ? { name: 'installation', params: { installation_id: installation.id } }
      : { name: 'installations' };
  }

  async function getInstallationByOfferID(id) {
    return await getInstallationByResourceID('offerId', id);
  }

  async function getInstallationInspection(id) {
    await getInstallations();
    const installation = installations.value.find((installation) => installation.id === Number(id));

    const inspectionId = installation.keuringOnOrder
      ? installation.orderId
      : installation.keuringOnInvoice
        ? installation.keuringsInvoiceId
        : NaN;

    const inspectionSource = installation.keuringOnOrder
      ? 'order'
      : installation.keuringOnInvoice
        ? 'invoice'
        : '';

    let inspectionStatus = InspectionStatuses.Requested;

    if (inspectionId && installation.approved === 1) {
      inspectionStatus = InspectionStatuses.Approved;
    }

    const netOperatorStatus = installation.manualNetOperatorRegistrationVerified;

    return {
      inspectionId,
      inspectionSource,
      inspectionStatus,
      netOperatorStatus,
    };
  }

  return {
    installations,
    refreshInstallations,
    getInstallations,
    getInstallationByID,
    getInstallationRouteByResourceID,
    getInstallationByOfferID,
    getInstallationInspection,
  };
});
